import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import * as Stream from 'effect/Stream';
import * as Schedule from 'effect/Schedule';
import { pipe } from 'effect/Function';
import { logger } from '@/lib/logger';

// Network Service interface
export interface NetworkService {
  readonly isOnline: Effect.Effect<boolean>;
  readonly waitForOnline: Effect.Effect<void>;
  readonly changes: Stream.Stream<boolean>;
  readonly withOnline: <A, E>(
    effect: Effect.Effect<A, E>
  ) => Effect.Effect<A, E | NetworkOfflineError>;
}

// Network errors
export class NetworkOfflineError {
  readonly _tag = 'NetworkOfflineError';
  constructor(readonly message: string = 'Network is offline') {}
}

// Network Service tag
export const NetworkService = Context.GenericTag<NetworkService>('@app/NetworkService');

// Helper to check online status
const checkOnline = Effect.sync(() => {
  if (typeof window !== 'undefined' && 'navigator' in window) {
    // Check if forced offline via localStorage (for testing)
    const forceOffline = localStorage.getItem('forceOffline') === 'true';
    if (forceOffline) {
      logger.debug('[NetworkService] FORCED OFFLINE via localStorage');
      return false;
    }

    const isOnline = navigator.onLine;
    logger.debug('[NetworkService] Network status', {
      isOnline,
      hostname: window.location.hostname
    });

    // Note: Browser DevTools "Offline" mode doesn't change navigator.onLine
    // It only blocks network requests. Use localStorage.setItem('forceOffline', 'true') for testing

    return isOnline;
  }
  return true; // Assume online in non-browser environment
});

// Create network status stream
const createNetworkStream = Stream.async<boolean>((emit) => {
  if (typeof window === 'undefined') {
    emit.single(true);
    return Effect.void;
  }

  const handleOnline = () => emit.single(true);
  const handleOffline = () => emit.single(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Emit initial status
  emit.single(navigator.onLine);

  return Effect.sync(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  });
});

// Network Service implementation
const networkServiceImpl: NetworkService = {
  isOnline: checkOnline,

  waitForOnline: pipe(
    checkOnline,
    Effect.flatMap(isOnline =>
      isOnline
        ? Effect.void
        : Effect.retry(
            Effect.fail(new NetworkOfflineError()),
            {
              schedule: Schedule.exponential('500 millis', 2).pipe(
                Schedule.upTo('30 seconds')
              ),
              while: () => Effect.map(checkOnline, online => !online)
            }
          )
    )
  ) as Effect.Effect<void>,

  changes: createNetworkStream,

  withOnline: <A, E>(effect: Effect.Effect<A, E>) =>
    pipe(
      checkOnline,
      Effect.flatMap(isOnline =>
        isOnline
          ? effect as Effect.Effect<A, E | NetworkOfflineError>
          : Effect.fail(new NetworkOfflineError()) as Effect.Effect<A, E | NetworkOfflineError>
      )
    ) as Effect.Effect<A, E | NetworkOfflineError>
};

// Create Network Layer
export const NetworkLayer = Layer.succeed(NetworkService, networkServiceImpl);

// Helper functions
export const network = {
  isOnline: () =>
    pipe(
      NetworkService,
      Effect.flatMap(service => service.isOnline)
    ),

  waitForOnline: () =>
    pipe(
      NetworkService,
      Effect.flatMap(service => service.waitForOnline)
    ),

  withOnline: <A, E>(effect: Effect.Effect<A, E>) =>
    pipe(
      NetworkService,
      Effect.flatMap(service => service.withOnline(effect))
    ),

  // Monitor network changes and log
  monitor: () =>
    pipe(
      NetworkService,
      Effect.flatMap(service =>
        Stream.runForEach(
          service.changes,
          (isOnline) =>
            Effect.log(`Network status changed: ${isOnline ? 'online' : 'offline'}`)
        )
      )
    )
};

// Connection quality monitoring
export interface ConnectionQuality {
  readonly type: 'unknown' | 'slow-2g' | '2g' | '3g' | '4g' | '5g' | 'wifi' | 'ethernet';
  readonly downlink: number; // Mbps
  readonly rtt: number; // Round-trip time in ms
  readonly saveData: boolean;
}

export const getConnectionQuality = Effect.sync((): ConnectionQuality => {
  if (typeof window !== 'undefined' && 'navigator' in window) {
    const nav = navigator as any;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

    if (connection) {
      return {
        type: connection.effectiveType || 'unknown',
        downlink: connection.downlink || 0,
        rtt: connection.rtt || 0,
        saveData: connection.saveData || false
      };
    }
  }

  return {
    type: 'unknown',
    downlink: 0,
    rtt: 0,
    saveData: false
  };
});

// Adaptive sync based on connection quality
export const shouldSyncNow = pipe(
  Effect.all([checkOnline, getConnectionQuality]),
  Effect.map(([isOnline, quality]) => {
    if (!isOnline) return false;

    // Don't sync on save data mode
    if (quality.saveData) return false;

    // Don't sync on very slow connections
    if (quality.type === 'slow-2g') return false;

    return true;
  })
);